// © 2025 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {HttpClient, HttpParams} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {finalize, forkJoin, map, Observable, of, shareReplay, tap} from 'rxjs';
import {
  CONTACT_QUICK_LINK_BATCH_SIZE,
  ContactChannelQuickLink,
  ContactQuickLinkEntry
} from './contact-quick-link.models';

/**
 * Reads the manager-facing contact quick links (#558): `GET /company/contact-link/for-employees?ids=a,b,c`.
 *
 * Server-gated on `ShardAdmin` / `CompanyOwner` / `PlatformOwner` / `ShiftLead`. Every caller ALSO gates
 * itself on {@link PermissionService.CanUseContactQuickLinks} before calling — not because the server would
 * be fooled, but because a screen that fires a guaranteed 403 on every load is a screen whose console is
 * always red and whose real failures are therefore invisible.
 *
 * Three behaviours, and each of them exists because of the shape of the screens that call it:
 *
 * 1. **Batching.** The endpoint caps a request at {@link CONTACT_QUICK_LINK_BATCH_SIZE} ids. A roster asks
 *    for whatever it renders and cannot know that number in advance, so the split happens here; a caller that
 *    had to remember the cap is a caller that forgets it on the one company with 240 employees.
 * 2. **A page-lifetime cache.** The chips are rendered by a roster, a detail header and a shift board that a
 *    manager moves between all evening, and the answer changes only when an employee links or unlinks a
 *    channel — minutes-to-days, never seconds. Caching per employee id (rather than per request) means the
 *    filter changes on the employees list, which re-derive the visible set on every keystroke, cost nothing
 *    after the first load. The cache lives as long as the Angular application instance, i.e. one page load;
 *    {@link clearCache} exists for the caller that knows it has invalidated an entry.
 * 3. **In-flight sharing.** Two components (or one component whose `effect` re-runs while a request is open)
 *    asking for the same id must not produce two requests. `DuplicateRequestInterceptor` does not help here —
 *    it deliberately collapses only *mutating* verbs — so the sharing is done by id, in this service, which
 *    is also the only place that knows a request for `[a, b]` already covers a later ask for `[b]`.
 *
 * **An empty answer is cached too.** An employee whose company holds no consent comes back as `[]`, and that
 * `[]` is stored — otherwise every render would re-ask for exactly the people the server has nothing to say
 * about, which on a roster of mostly-unlinked staff is every request the page makes.
 */
@Injectable({providedIn: 'root'})
export class ContactQuickLinkHttpService {
  /** Resolved channels per employee id. An entry that is present is authoritative, `[]` included. */
  private readonly cache = new Map<string, ContactChannelQuickLink[]>();

  /**
   * The request that will fill each not-yet-cached id, while it is open. Shared (`shareReplay`) so a second
   * asker rides the first request rather than starting its own, and removed in `finalize` so a failed request
   * does not pin an id to a permanent error.
   */
  private readonly inFlight = new Map<string, Observable<void>>();

  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get endpoint(): string {
    return `${this.baseUrl}/company/contact-link/for-employees`;
  }

  /**
   * The consented channels of `ids`, keyed by employee id.
   *
   * Returns a **map** rather than the wire array because every placement wants a lookup — a roster row asks
   * "what does this employee have", never "give me the third entry". Ids the server omitted resolve to an
   * empty array, so a caller never has to tell "not loaded" from "nothing to show" by the absence of a key.
   *
   * Emits once and completes. Failures propagate: a chip column is worth losing quietly (the callers all
   * swallow it), but a service that swallowed the error itself could never be told apart from one that
   * returned nothing legitimately.
   */
  forEmployees(ids: readonly string[]): Observable<ReadonlyMap<string, ContactChannelQuickLink[]>> {
    const wanted = [...new Set(ids.filter(id => !!id))];
    if (wanted.length === 0) return of(new Map<string, ContactChannelQuickLink[]>());

    const waits: Observable<void>[] = [];
    const fresh: string[] = [];

    for (const id of wanted) {
      if (this.cache.has(id)) continue;
      const pending = this.inFlight.get(id);
      // Reference equality, not id equality: one shared request covers a whole chunk, and adding it twice
      // would make forkJoin wait for the same observable twice for no benefit.
      if (pending) {
        if (!waits.includes(pending)) waits.push(pending);
        continue;
      }
      fresh.push(id);
    }

    for (let index = 0; index < fresh.length; index += CONTACT_QUICK_LINK_BATCH_SIZE) {
      const chunk = fresh.slice(index, index + CONTACT_QUICK_LINK_BATCH_SIZE);
      const request = this.fetch(chunk);
      for (const id of chunk) this.inFlight.set(id, request);
      waits.push(request);
    }

    if (waits.length === 0) return of(this.snapshot(wanted));
    return forkJoin(waits).pipe(map(() => this.snapshot(wanted)));
  }

  /**
   * Forget everything read so far. For the caller that has just changed what the answer would be — nothing
   * does today, which is why this is a method and not an event: an invalidation channel nobody publishes on
   * is a channel that rots.
   */
  clearCache(): void {
    this.cache.clear();
  }

  /** One request for one chunk of ids, writing straight into the cache. */
  private fetch(chunk: readonly string[]): Observable<void> {
    // Through HttpParams rather than string concatenation so an id containing a reserved character is encoded
    // once and correctly. Angular's default codec leaves `,` literal (it is a valid sub-delimiter), which is
    // what keeps the URL readable in the network tab and matches the contract's `ids=a,b,c` exactly.
    const params = new HttpParams().set('ids', chunk.join(','));

    return this.http.get<ContactQuickLinkEntry[]>(this.endpoint, {params}).pipe(
      tap(entries => {
        for (const entry of entries ?? []) {
          this.cache.set(entry.employeeId, entry.channels ?? []);
        }
        // An id the response did not name has no consented channel. Recording that as `[]` is what stops the
        // next render asking again — without it the cache would only ever cover the linked half of a roster.
        for (const id of chunk) {
          if (!this.cache.has(id)) this.cache.set(id, []);
        }
      }),
      map(() => undefined),
      // Cleared on success AND on error: an id left pointing at a failed request would replay that error to
      // every later asker for the life of the page.
      finalize(() => {
        for (const id of chunk) this.inFlight.delete(id);
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );
  }

  /** The cached answer for exactly `ids`, with anything still unknown reported as having no channels. */
  private snapshot(ids: readonly string[]): ReadonlyMap<string, ContactChannelQuickLink[]> {
    const result = new Map<string, ContactChannelQuickLink[]>();
    for (const id of ids) result.set(id, this.cache.get(id) ?? []);
    return result;
  }
}
